/*
  Glamora Beauty — expérience immersive 2026.
  - vidéo de la bannière adaptée à l'écran (jamais lancée si l'utilisateur réduit les animations)
  - bouton pause / lecture, bouton « Explorer »
  - apparitions au défilement : sections, listes séquencées, cartes produits
  - parallaxe légère (ordinateur uniquement)
  - fondu de couleur entre deux sections de teintes différentes
  - en-tête translucide qui évolue au défilement
  - reflet des surfaces « verre » qui suit le pointeur (souris uniquement)
  Rien n'est masqué sans ce script : il ajoute lui-même les marqueurs qui déclenchent les animations.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const root = document.documentElement;
  const motionOK = () => !reduceMotion.matches;

  /* ---------- Vidéo de la bannière ---------- */
  function setupHeroVideos(scope = document) {
    for (const hero of scope.querySelectorAll('.gl-hero')) {
      if (hero.dataset.glVideoReady) continue;
      hero.dataset.glVideoReady = 'true';

      const wrappers = [...hero.querySelectorAll('[data-gl-video]')];
      const toggle = hero.querySelector('[data-gl-video-toggle]');
      const label = toggle?.querySelector('[data-gl-video-label]');
      if (!wrappers.length) continue;

      let current = null;
      let pausedByUser = reduceMotion.matches;

      const updateToggle = () => {
        if (!toggle || !current) return;
        const paused = current.querySelector('video')?.paused ?? true;
        toggle.setAttribute('aria-pressed', String(paused));
        if (label) label.textContent = paused ? label.dataset.play : label.dataset.pause;
      };

      const play = async () => {
        const video = current?.querySelector('video');
        if (!video) return;
        video.muted = true;
        try {
          await video.play();
          current.classList.add('is-playing');
        } catch (error) {
          // Lecture refusée (économiseur de données, format non pris en charge) : l'image reste affichée.
          current.classList.remove('is-playing');
        }
        updateToggle();
      };

      const select = () => {
        const next = wrappers.find((w) => window.matchMedia(w.dataset.glVideo).matches) || null;
        if (next === current) return;
        if (current) {
          current.querySelector('video')?.pause();
          current.classList.remove('is-playing');
        }
        current = next;
        if (toggle) toggle.hidden = !current;
        if (current && !pausedByUser) play();
        updateToggle();
      };

      toggle?.addEventListener('click', () => {
        const video = current?.querySelector('video');
        if (!video) return;
        if (video.paused) {
          pausedByUser = false;
          play();
        } else {
          pausedByUser = true;
          video.pause();
          updateToggle();
        }
      });

      // Économise la batterie : la vidéo s'arrête quand la bannière sort de l'écran.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          const video = current?.querySelector('video');
          if (!video) return;
          if (!entry.isIntersecting) video.pause();
          else if (!pausedByUser) play();
        }).observe(hero);
      }

      for (const w of wrappers) {
        window.matchMedia(w.dataset.glVideo).addEventListener('change', select);
      }
      // Laisse la page se charger avant de télécharger la vidéo.
      if (document.readyState === 'complete') select();
      else window.addEventListener('load', select, { once: true });
    }
  }

  /* ---------- Bouton « Explorer » ---------- */
  function setupScrollCue(scope = document) {
    for (const cue of scope.querySelectorAll('[data-gl-scroll]')) {
      if (cue.dataset.glReady) continue;
      cue.dataset.glReady = 'true';
      cue.addEventListener('click', (event) => {
        const target = document.querySelector(cue.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: motionOK() ? 'smooth' : 'auto', block: 'start' });
        target.focus({ preventScroll: true });
      });
    }
  }

  /* ---------- Apparitions ---------- */
  let revealObserver = null;

  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.04 }
    );
    return revealObserver;
  }

  const belowFold = (el) => el.getBoundingClientRect().top > window.innerHeight * 0.92;

  function setupReveal(scope = document) {
    if (!motionOK() || !('IntersectionObserver' in window)) return;
    const observer = getRevealObserver();
    root.classList.add('gl-motion');

    // Sections de l'accueil (sauf la première, visible immédiatement).
    if (document.body.dataset.glTemplate === 'index') {
      for (const section of scope.querySelectorAll('#MainContent > .shopify-section:not(:first-child)')) {
        if (section.hasAttribute('data-gl-reveal') || !belowFold(section)) continue;
        section.setAttribute('data-gl-reveal', '');
        observer.observe(section);
      }
    }

    // Listes séquencées : chaque enfant apparaît avec un léger décalage.
    const lists = new Set(scope.querySelectorAll('[data-gl-stagger]'));
    // Cartes produits : on observe la liste (et non chaque carte) pour que les carrousels horizontaux
    // révèlent aussi les cartes hors champ.
    for (const item of scope.querySelectorAll('.resource-list__item, .product-grid__item')) {
      if (item.parentElement) lists.add(item.parentElement);
    }

    for (const list of lists) {
      if (list.hasAttribute('data-gl-stagger-ready')) continue;
      list.setAttribute('data-gl-stagger-ready', '');
      if (!belowFold(list)) continue;
      list.setAttribute('data-gl-armed', '');
      [...list.children].forEach((child, index) => {
        child.style.setProperty('--gl-delay', `${Math.min(index, 7) * 80}ms`);
      });
      observer.observe(list);
    }
  }

  /* ---------- Fondu de couleur entre sections ---------- */
  function backgroundOf(section) {
    const el =
      section.querySelector(':scope > .section-background') ||
      section.querySelector(':scope > [class*="color-scheme-"]') ||
      section.querySelector(':scope > * > .section-background');
    if (!el) return null;
    const color = getComputedStyle(el).backgroundColor;
    if (!color || color === 'transparent' || /rgba\(0, 0, 0, 0\)/.test(color)) return null;
    return { el, color };
  }

  function luminance(color) {
    const [r, g, b] = (color.match(/[\d.]+/g) || [0, 0, 0]).map(Number);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function setupBridges() {
    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')];
    let previous = null;
    for (const section of sections) {
      const current = backgroundOf(section);
      if (!current) {
        previous = null;
        continue;
      }
      current.el.classList.remove('gl-bridge');
      if (previous && previous.color !== current.color && !section.querySelector('.gl-hero')) {
        // Le fondu reste dans la marge haute de la section pour ne jamais passer sous un texte.
        const sectionTop = section.getBoundingClientRect().top;
        const firstContent = section.querySelector('h1, h2, h3, p, a, img, .gl-section-head, .resource-list__item');
        const room = firstContent ? firstContent.getBoundingClientRect().top - sectionTop : 0;
        // Entre une teinte sombre et une teinte claire, le fondu est plus court pour ne pas faire « ombre ».
        const contrast = Math.abs(luminance(previous.color) - luminance(current.color));
        const height = Math.max(0, Math.min(contrast > 0.5 ? 56 : 120, Math.round(room * 0.85)));
        if (height >= 24) {
          current.el.style.setProperty('--gl-prev-bg', previous.color);
          current.el.style.setProperty('--gl-bridge-h', `${height}px`);
          current.el.classList.add('gl-bridge');
        }
      }
      previous = current;
    }
  }

  /* ---------- Défilement : en-tête, bannière, parallaxe ---------- */
  const parallaxItems = new Set();
  let parallaxObserver = null;
  let hero = null;
  let heroParts = null;
  let ticking = false;

  function setupParallax(scope = document) {
    hero = document.querySelector('[data-gl-hero]');
    heroParts = hero && {
      media: hero.querySelector('[data-gl-hero-media]'),
      content: hero.querySelector('[data-gl-hero-content]'),
      picks: [...hero.querySelectorAll('[data-gl-pick]')],
    };

    if (!('IntersectionObserver' in window)) return;
    parallaxObserver ??= new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) parallaxItems.add(entry.target);
        else parallaxItems.delete(entry.target);
      }
      requestTick();
    });
    for (const el of scope.querySelectorAll('[data-gl-parallax]')) parallaxObserver.observe(el);
  }

  function parallaxActive() {
    return motionOK() && desktop.matches;
  }

  function update() {
    ticking = false;
    const y = window.scrollY;
    root.classList.toggle('gl-scrolled', y > 8);

    const heroHeight = hero ? hero.offsetHeight : 0;
    root.classList.toggle('gl-past-hero', !hero || y > heroHeight - 80);
    root.classList.toggle('gl-parallax', parallaxActive());

    if (!parallaxActive()) return;

    if (hero && y < heroHeight) {
      const progress = Math.min(1, y / heroHeight);
      heroParts.media?.style.setProperty('transform', `translate3d(0, ${(y * 0.28).toFixed(1)}px, 0)`);
      if (heroParts.content) {
        heroParts.content.style.transform = `translate3d(0, ${(y * -0.08).toFixed(1)}px, 0)`;
        heroParts.content.style.opacity = String(Math.max(0, 1 - progress * 1.35).toFixed(3));
      }
      heroParts.picks.forEach((pick, index) => {
        pick.style.setProperty('--gl-lift', `${(y * -(0.12 + index * 0.07)).toFixed(1)}px`);
      });
    }

    const vh = window.innerHeight;
    for (const el of parallaxItems) {
      const rect = el.getBoundingClientRect();
      // Position de l'élément dans l'écran, de -1 (en bas) à 1 (en haut) ; le déplacement reste
      // dans la marge créée par l'agrandissement de l'image (voir glamora-immersive.css).
      const progress = Math.max(-1, Math.min(1, (vh / 2 - (rect.top + rect.height / 2)) / (vh / 2 + rect.height / 2)));
      const factor = parseFloat(el.dataset.glParallax) || 0.1;
      el.style.setProperty('--gl-py', `${(progress * rect.height * Math.min(factor, 0.05)).toFixed(1)}px`);
    }
  }

  function requestTick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function resetParallax() {
    if (parallaxActive()) return requestTick();
    if (heroParts) {
      heroParts.media?.style.removeProperty('transform');
      heroParts.content?.style.removeProperty('transform');
      heroParts.content?.style.removeProperty('opacity');
      heroParts.picks.forEach((pick) => pick.style.removeProperty('--gl-lift'));
    }
    for (const el of document.querySelectorAll('[data-gl-parallax]')) el.style.removeProperty('--gl-py');
    requestTick();
  }

  /* ---------- Reflet des surfaces « verre » ---------- */
  function setupGlass() {
    document.addEventListener(
      'pointermove',
      (event) => {
        if (!finePointer.matches || !motionOK()) return;
        const glass = event.target.closest?.('.gl-glass');
        if (!glass) return;
        const rect = glass.getBoundingClientRect();
        glass.style.setProperty('--gl-mx', `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
        glass.style.setProperty('--gl-my', `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
      },
      { passive: true }
    );
  }

  /* ---------- Démarrage ---------- */
  const init = () => {
    root.classList.add('gl-js');
    setupHeroVideos();
    setupScrollCue();
    setupReveal();
    setupParallax();
    setupGlass();
    // Les couleurs calculées ne sont fiables qu'une fois les feuilles de style appliquées.
    requestAnimationFrame(setupBridges);
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick, { passive: true });
    reduceMotion.addEventListener('change', resetParallax);
    desktop.addEventListener('change', resetParallax);
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Éditeur de thème : réinitialise ce qui dépend de la section rechargée.
  document.addEventListener('shopify:section:load', (event) => {
    setupHeroVideos(event.target);
    setupScrollCue(event.target);
    setupReveal(event.target);
    setupParallax(event.target);
    setupBridges();
  });

})();
